/*  RetakeCourseDialog — premium confirmation modal for course retake.
    ────────────────────────────────────────────────────────────────────────
    Replaces the browser-default `window.confirm` used previously. Explains
    the wipe + certificate-preservation semantics with a warm, premium
    aesthetic that matches the rest of the Academy surface.

    Props:
      open         : bool
      onOpenChange : (open: bool) => void
      courseTitle  : string
      onConfirm    : () => Promise<void>  — the caller does the actual API call
*/
import React, { useState } from "react";
import { ClockCounterClockwise, Trophy, ArrowRight, X } from "@phosphor-icons/react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";


export default function RetakeCourseDialog({ open, onOpenChange, courseTitle, onConfirm }) {
    const [busy, setBusy] = useState(false);

    const handleConfirm = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await onConfirm?.();
            onOpenChange?.(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="p-0 max-w-md overflow-hidden gap-0"
                data-testid="retake-course-dialog"
            >
                {/* Hero */}
                <div className="relative bg-gradient-to-br from-amber-50 via-white to-emerald-50 px-7 pt-8 pb-5">
                    <div className="w-14 h-14 rounded-2xl bg-white border border-amber-200 grid place-items-center shadow-sm mb-4">
                        <ClockCounterClockwise size={26} weight="duotone" className="text-amber-700" />
                    </div>
                    <DialogHeader className="text-left space-y-1.5">
                        <DialogTitle className="font-display text-[19px] font-bold text-slate-900 leading-snug tracking-tight" data-testid="retake-course-dialog-title">
                            Retake this course?
                        </DialogTitle>
                        <DialogDescription className="text-[13px] text-slate-500 leading-relaxed">
                            You&apos;re about to restart{" "}
                            <span className="font-semibold text-slate-800">
                                {courseTitle || "this course"}
                            </span>
                            {" "}from Module 1.
                        </DialogDescription>
                    </DialogHeader>
                </div>

                {/* Body — what happens next */}
                <div className="px-7 py-5 bg-white space-y-3">
                    <RetakeRow
                        tint="amber"
                        title="Progress will be wiped"
                        body="All lesson completions, quiz attempts, and final exam scores will be reset to zero."
                    />
                    <RetakeRow
                        tint="emerald"
                        title="Your certificate is safe"
                        body="The certificate from your first pass stays in your wallet as immutable proof."
                        icon={<Trophy size={12} weight="fill" />}
                    />
                    <RetakeRow
                        tint="slate"
                        title="Improve your score"
                        body="Quizzes track your best attempt — retaking is a great way to sharpen your mastery."
                    />
                </div>

                {/* Actions */}
                <div className="px-7 pb-6 pt-2 bg-white flex items-center gap-2 justify-end border-t border-slate-100">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange?.(false)}
                        disabled={busy}
                        className="h-9 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        data-testid="retake-course-cancel"
                    >
                        <X size={13} weight="bold" className="mr-1" />
                        Not now
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={busy}
                        className="h-9 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                        data-testid="retake-course-confirm"
                    >
                        {busy ? "Resetting…" : "Start fresh run"}
                        {!busy && <ArrowRight size={13} weight="bold" className="ml-1.5" />}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function RetakeRow({ tint, title, body, icon }) {
    const chipCls = {
        amber:   "bg-amber-50 text-amber-700 border-amber-200",
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        slate:   "bg-slate-100 text-slate-600 border-slate-200",
    }[tint] || "bg-slate-100 text-slate-600 border-slate-200";

    return (
        <div className="flex items-start gap-3">
            <div className={`shrink-0 w-6 h-6 rounded-full border grid place-items-center ${chipCls}`}>
                {icon || <span className="w-1.5 h-1.5 rounded-full bg-current" />}
            </div>
            <div className="min-w-0">
                <div className="text-[13px] font-semibold text-slate-900 leading-snug">{title}</div>
                <div className="text-[12px] text-slate-500 leading-snug mt-0.5">{body}</div>
            </div>
        </div>
    );
}
